import { ContentStack, Content } from "pdfmake/interfaces";
import {
  DEFAULT_FONT_SIZES,
  getCheckupReportFooter,
  getCheckupReportHeader,
  ITDocumentDefinitions,
} from "./CheckupReportCommon";
import { CheckupReportCover, CheckupReportFooter } from "./CheckupReportType";

// Utils
import moment from "moment";

// Types
export type FormDoctorRecommendationReportProps = {
  coverData: CheckupReportCover;
  doctorNoteData: CheckupReportDoctorNote;
  language: "th" | "en";
  printer: string;
};

export type CheckupReportDoctorNote = {
  doctorNoteArray: Content[];
  doctor?: string;
  doctor_number?: string;
};

/*================================ DOCTOR NOTE SECTION ==============================*/

const generateDoctorNotePage = (
  doctorNoteData: CheckupReportDoctorNote,
  language: "th" | "en"
): ContentStack => {
  const isEng = language === "en";

  return {
    stack: [
      {
        text: isEng ? "Doctor's Recommendation" : "คำแนะนำจากแพทย์",
        style: "page_title",
      },
      ...doctorNoteData.doctorNoteArray,
      {
        table: {
          widths: ["60%", "25%", "15%"],
          body: [
            [
              {
                text: isEng
                  ? "The results have been reviewed and interpreted by certified physician."
                  : "ผลตรวจได้รับการตรวจสอบ และแปลผลโดยแพทย์ผู้เชี่ยวชาญ",
                bold: true,
                marginTop: 10,
                colSpan: 3,
              },
              ...Array(2).fill({}),
            ],
            [
              {
                text: doctorNoteData?.doctor || "",
              },
              {
                text: isEng ? "Medical License No." : "เลขที่ใบประกอบวิชาชีพ",
              },
              {
                text: doctorNoteData?.doctor_number || "",
                alignment: "center",
              },
            ],
          ],
        },
        layout: {
          vLineWidth: () => 0,
          hLineWidth: (i, node) => (i === 0 ? 1 : 0),
          hLineStyle: (i, node) => {
            if (i === 0) {
              return { dash: { length: 4, space: 2 } };
            }
            return null;
          },
        },
        marginTop: 10,
      },
    ],
  };
};

/*================================ MAIN ==============================*/

const FormDoctorRecommendationReport = async (
  props: FormDoctorRecommendationReportProps
): Promise<ITDocumentDefinitions> => {
  const { coverData, doctorNoteData, printer, language = "th" } = props;

  const currentDate = moment();

  const footerData: CheckupReportFooter = {
    currentDate,
    printer,
  };

  const headerForm = await getCheckupReportHeader({
    detail: coverData.cover,
    isMainReport: false,
    language,
  });

  const { font, fontSizes, images, lineHeights, styles } = headerForm;

  return {
    pageSize: "A4",
    defaultStyle: {
      font,
      fontSize: fontSizes[DEFAULT_FONT_SIZES],
      lineHeight: lineHeights[1],
    },
    ...headerForm,
    images: { ...images },
    styles: {
      ...styles,
      cover_title: {
        fontSize: fontSizes[20],
        alignment: "center",
        bold: true,
        marginBottom: 30,
      },
      cover_logo: {
        alignment: "center",
        marginBottom: 30,
      },
      cover_package: {
        alignment: "center",
        margin: [20, 10, 20, 10],
      },
      page_title: {
        fontSize: fontSizes[16],
        bold: true,
        marginBottom: 10,
      },
      footer_title: {
        fontSize: fontSizes[12],
      },
      footer_info: {
        fontSize: fontSizes[11],
      },
    },
    content: [generateDoctorNotePage(doctorNoteData, language)],
    footer: (currentPage, countPage) =>
      getCheckupReportFooter({
        currentPage,
        countPage,
        footerData,
        language,
        isMainReport: false,
      }),
  };
};

export default FormDoctorRecommendationReport;
